import i18next from 'i18next';
import { SagaIterator } from 'redux-saga';
import { all, put, call, select, takeEvery } from 'redux-saga/effects';
import {
  PapiDaSharedDeviationSubmitGroupDestinations,
  PapiDaSharedDeviationSubmitInit,
} from '@wix/da-papi-types';
import { isApiError, requestPuppy } from '@wix/da-http-client';
import { getCurrentUser } from '@wix/da-shared-react/pkg/publicSession/selectors';
import { normalize } from '@wix/da-shared-react/pkg/redux/normalizr';
import { deviationSchema } from '@wix/da-shared-react/pkg/redux/normalizr/schemas/deviation';
import { galleryFolderSchema } from '@wix/da-shared-react/pkg/redux/normalizr/schemas/folder';
import { putErrorNotification } from '@wix/da-shared-react/pkg/utils/saga';
import {
  uploadFileImmediately,
  uploadFailure,
  uploadSuccess,
  uploadAttachmentImmediately,
  uploadAttachmentSuccess,
  uploadAttachmentFailure,
  cancelAttachmentUpload,
} from '@wix/da-shared-react/pkg/redux/uploads/actions';
import {
  initialize,
  initializeSuccess,
  initializeTierTags,
  initializeTierTagsSuccess,
  initializeTierTagsError,
  refresh,
  refreshSuccess,
  groupDestinationsFetch,
  groupDestinationsFetchSuccess,
  groupDestinationsFetchError,
  deviationSaveError,
  setUploadStatus,
} from '../actions';
import { fakeInitResponse, fakeTierResponse } from '../fake';
import { SUBMIT_POPUP_ERROR_CODE } from '../constants';

function* throwError(errorMessage: string, errorCode: SUBMIT_POPUP_ERROR_CODE) {
  // T63499: Show toaster notification unless it's a quota error
  if (errorCode !== SUBMIT_POPUP_ERROR_CODE.ITEM_COUNT_QUOTA_LIMIT_REACHED) {
    yield putErrorNotification(errorMessage);
  }
  yield put(deviationSaveError(errorMessage, errorCode));
}

function* fetchSubmitInitialization(deviationId: number | string) {
  const params = { deviationid: deviationId };
  const response = yield call(
    requestPuppy,
    {
      method: 'get',
      url: '/deviation/submit/init',
      params,
    },
    fakeInitResponse
  );

  if (isApiError(response)) {
    yield call(
      throwError,
      response.errorDescription,
      SUBMIT_POPUP_ERROR_CODE.DEVIATION_INITIALIZATION_ERROR
    );
    return;
  }

  if (!response.deviation?.deviationId) {
    yield call(
      throwError,
      i18next.t('deviationSubmit.error.initDeviationServerError'),
      SUBMIT_POPUP_ERROR_CODE.DEVIATION_INITIALIZATION_ERROR
    );
    return;
  }

  return response;
}

function normalizeInitializationResponse(response) {
  return normalize(response, {
    deviation: deviationSchema,
    tiers: [deviationSchema],
    inTiers: [deviationSchema],
    galleries: [galleryFolderSchema],
    inGalleries: [galleryFolderSchema],
  });
}

function* initializeSaga(action: ReturnType<typeof initialize>): SagaIterator {
  const {
    payload: { deviationId },
  } = action;

  const response: PapiDaSharedDeviationSubmitInit = yield call(
    fetchSubmitInitialization,
    deviationId
  );
  if (!response) {
    return;
  }

  // Override original response with the normalized results
  const { result, entities } = normalizeInitializationResponse(response);
  yield put(
    initializeSuccess({
      ...response,
      ...result,
      entities,
      scheduledPublishTime: response.deviation?.scheduledPublishTime,
    })
  );
}

function* refreshSaga(action: ReturnType<typeof refresh>): SagaIterator {
  const {
    payload: { deviationId },
  } = action;

  const response = yield call(fetchSubmitInitialization, deviationId);
  if (!response) {
    return;
  }

  // Override original response with the normalized results
  const { result, entities } = normalizeInitializationResponse(response);
  yield put(refreshSuccess({ ...response, ...result, entities }));
}

function* initializeTierTagsSaga(action: ReturnType<typeof initializeTierTags>): SagaIterator {
  const {
    payload: { tierId: deviationid, onError },
  } = action;
  if (!deviationid) {
    return;
  }
  const { username } = yield select(getCurrentUser);
  const params = { deviationid, username };
  const response = yield call(
    requestPuppy,
    {
      method: 'get',
      url: '/tier',
      params,
    },
    fakeTierResponse
  );

  if (isApiError(response)) {
    yield put(initializeTierTagsError(response.errorDescription));
    yield putErrorNotification(response.errorDescription);

    if (onError) {
      onError(response.errorDescription);
    }
    return;
  }
  yield put(initializeTierTagsSuccess({ tierId: deviationid, tags: response.tags }));
}

function* handleGroupDestinationsFetch({ payload: { deviationId } }) {
  const result: PapiDaSharedDeviationSubmitGroupDestinations = yield call(requestPuppy, {
    method: 'post',
    url: 'deviation/submit/group_destinations',
    data: {
      deviationid: deviationId,
    },
  });

  if (result) {
    yield put(groupDestinationsFetchSuccess(result));
  } else {
    yield put(groupDestinationsFetchError());
  }
}

function* handleMainUploadStarted() {
  yield put(setUploadStatus('main', 'uploading'));
}
function* handleMainUploadEnded() {
  yield put(setUploadStatus('main', 'idle'));
}
function* handleAttachmentUploadStarted({
  payload,
}: ReturnType<typeof uploadAttachmentImmediately>) {
  // Multi-image deviation uses the same backend as pcp/exclusives,
  // but in the context of the form we treat them separately
  const isPcp = payload.type === 'pcp';
  const isExclusive = payload.type === 'exclusive';
  yield put(setUploadStatus(isPcp ? 'pcp' : isExclusive ? 'exclusive' : 'main', 'uploading'));
}
function* handleAttachmentUploadEnded({ payload }) {
  const isPcp = payload.type === 'pcp';
  const isExclusive = payload.type === 'exclusive';
  yield put(setUploadStatus(isPcp ? 'pcp' : isExclusive ? 'exclusive' : 'main', 'idle'));
}

export default function* modalSaga() {
  yield all([
    takeEvery(initialize, initializeSaga),
    takeEvery(initializeTierTags, initializeTierTagsSaga),
    takeEvery(refresh, refreshSaga),
    takeEvery(groupDestinationsFetch, handleGroupDestinationsFetch),
    takeEvery(uploadFileImmediately, handleMainUploadStarted),
    takeEvery(uploadSuccess, handleMainUploadEnded),
    takeEvery(uploadFailure, handleMainUploadEnded),
    takeEvery(uploadAttachmentImmediately, handleAttachmentUploadStarted),
    takeEvery(uploadAttachmentSuccess, handleAttachmentUploadEnded),
    takeEvery(uploadAttachmentFailure, handleAttachmentUploadEnded),
    takeEvery(cancelAttachmentUpload, handleAttachmentUploadEnded),
  ]);
}
